/**
 * ForgotPasswordForm - Forgot Password Form Component
 *
 * Pure presentation component for forgot password functionality.
 * Handles email input, validation, and submission.
 *
 * Features:
 * - Email validation
 * - Loading state
 * - Error/success message display
 * - Link back to login
 *
 * Used in: ForgotPasswordTemplate
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';

export interface ForgotPasswordFormProps {
    /** Callback when form is submitted with email */
    onSubmit: (email: string) => Promise<void>;

    /** Loading state during submission */
    isLoading?: boolean;

    /** Platform flag for styling (web vs plugin) */
    isWeb?: boolean;

    /** Initial error message to display */
    initialError?: string;

    /** Callback to navigate back to login */
    onBackToLogin?: () => void;
}

interface FormErrors {
    email?: string;
    general?: string;
}

/**
 * ForgotPasswordForm Component
 *
 * Handles the email input and validation for password reset requests.
 */
export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
    onSubmit,
    isLoading = false,
    isWeb = true,
    initialError,
    onBackToLogin,
}) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState<FormErrors>({});
    const [successMessage, setSuccessMessage] = useState('');

    // Validate email format
    const validateEmail = useCallback((email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }, []);

    // Validate form
    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};

        if (!email.trim()) {
            newErrors.email = t('auth.emailRequired');
        } else if (!validateEmail(email)) {
            newErrors.email = t('auth.invalidEmail');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [email, validateEmail, t]);

    // Handle email input change
    const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        // Clear field-specific error
        if (errors.email) {
            setErrors(prev => ({ ...prev, email: undefined }));
        }
        // Clear general error
        if (errors.general) {
            setErrors(prev => ({ ...prev, general: undefined }));
        }
        // Clear success message
        if (successMessage) {
            setSuccessMessage('');
        }
    }, [errors.email, errors.general, successMessage]);

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm() || isLoading) {
            return;
        }

        try {
            await onSubmit(email);
            // Show success message
            setSuccessMessage(t('auth.resetInstructionsSent'));
            // Clear form
            setEmail('');
            setErrors({});
        } catch (error: any) {
            setErrors({ general: error.message || t('auth.sendResetFailed') });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-full space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    {t('auth.emailAddress')}
                </Label>
                <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.enterEmailAddress')}
                    value={email}
                    onChange={handleEmailChange}
                    className={`w-full ${errors.email ? 'border-red-500' : ''}`}
                    disabled={isLoading}
                    autoComplete="email"
                />
                {errors.email && (
                    <p className="text-xs text-red-600">{errors.email}</p>
                )}
            </div>

            {/* Submit Button */}
            <Button
                type="submit"
                variant="primary"
                size={isWeb ? 'xl' : 'lg'}
                className="w-full"
                disabled={isLoading}
            >
                {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('auth.sending')}
                    </span>
                ) : (
                    t('auth.sendResetLink')
                )}
            </Button>
        </form>
    );
};
